import { useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Crosshair, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const FIELDS = [["offset_x", "offset_y", "Front"], ["back_offset_x", "back_offset_y", "Back"]];

const Nudge = ({ p, kx, ky, label, set, step }) => (
  <div className="space-y-1.5">
    <p className="text-[10px] font-mono uppercase text-slate-500">{label} offset</p>
    <div className="flex items-center gap-2">
      <div className="grid grid-cols-3 gap-0.5 w-[78px]">
        <span />
        <NB onClick={() => set(ky, p[ky] - step)} t={`nudge-${label}-up`}><ArrowUp size={12} /></NB>
        <span />
        <NB onClick={() => set(kx, p[kx] - step)} t={`nudge-${label}-left`}><ArrowLeft size={12} /></NB>
        <span className="grid place-items-center text-[9px] text-slate-600">●</span>
        <NB onClick={() => set(kx, p[kx] + step)} t={`nudge-${label}-right`}><ArrowRight size={12} /></NB>
        <span />
        <NB onClick={() => set(ky, p[ky] + step)} t={`nudge-${label}-down`}><ArrowDown size={12} /></NB>
        <span />
      </div>
      <div className="space-y-1 flex-1">
        {[[kx, "X"], [ky, "Y"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-400">{l}
            <input type="number" step="0.1" value={p[k]} onChange={(e) => set(k, +e.target.value || 0)} className="cf-input h-7 text-xs font-mono" data-testid={`printer-${k}-input`} />
          </label>
        ))}
      </div>
    </div>
  </div>
);

const NB = ({ children, onClick, t }) => (
  <button onClick={onClick} data-testid={t} className="h-6 grid place-items-center rounded bg-slate-950 border border-slate-800 hover:border-amber-500/60 text-slate-300">{children}</button>
);

export default function PrinterPanel({ printers, setPrinters, printerId, setPrinterId, onCalibrate, busy }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0.1);
  const p = printers.find((x) => x.id === printerId);

  const set = (k, v) => setPrinters((ps) => ps.map((x) => (x.id === printerId ? { ...x, [k]: Math.round(v * 100) / 100 } : x)));

  const create = async () => {
    if (!name.trim()) return;
    const { data } = await api.post("/printers", { name: name.trim() });
    setPrinters((ps) => [...ps, data]);
    setPrinterId(data.id);
    setName("");
    toast.success("Printer profile created");
  };
  const save = async () => {
    setSaving(true);
    try {
      const { name: n, offset_x, offset_y, back_offset_x, back_offset_y, scale } = p;
      await api.put(`/printers/${p.id}`, { name: n, offset_x, offset_y, back_offset_x, back_offset_y, scale });
      toast.success("Calibration saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    await api.delete(`/printers/${p.id}`);
    setPrinters((ps) => ps.filter((x) => x.id !== p.id));
    setPrinterId("");
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4" data-testid="printer-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Printer calibration</p>
      <select className="cf-input h-9 text-xs" value={printerId} onChange={(e) => setPrinterId(e.target.value)} data-testid="printer-select">
        <option value="">No calibration (exact position)</option>
        {printers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); create(); }}>
        <input className="cf-input h-8 text-xs" placeholder="New profile, e.g. Evolis Primacy" value={name} onChange={(e) => setName(e.target.value)} data-testid="printer-name-input" />
        <button className="cf-btn-ghost h-8 text-xs shrink-0" data-testid="printer-create-button"><Plus size={13} /> Add</button>
      </form>
      {p && (
        <div className="space-y-4" data-testid="printer-editor">
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            Nudge step
            <div className="flex gap-1">
              {[0.1, 0.5, 1].map((s) => (
                <button key={s} onClick={() => setStep(s)} data-testid={`nudge-step-${s}`}
                  className={`px-2 h-6 rounded text-[10px] font-mono ${step === s ? "bg-amber-500/20 text-amber-300" : "bg-slate-950 text-slate-400"}`}>{s}mm</button>
              ))}
            </div>
          </div>
          {FIELDS.map(([kx, ky, label]) => <Nudge key={label} p={p} kx={kx} ky={ky} label={label} set={set} step={step} />)}
          <label className="flex items-center gap-2 text-[11px] text-slate-400">Scale %
            <input type="number" step="0.1" value={p.scale} onChange={(e) => set("scale", +e.target.value || 100)} className="cf-input h-7 text-xs font-mono" data-testid="printer-scale-input" />
          </label>
          <p className="text-[11px] text-slate-500 leading-relaxed">Print the calibration card on a blank. If the red edge line is cut off on the left, nudge right by the ring number you see first. Positive X = right, positive Y = down.</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-2">
            <button className="cf-btn-ghost h-9 text-xs" onClick={onCalibrate} disabled={busy} data-testid="print-calibration-button">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Crosshair size={13} />} Calibration card
            </button>
            <button className="cf-btn-primary h-9 text-xs px-3" onClick={save} disabled={saving} data-testid="printer-save-button"><Save size={13} /> Save</button>
            <button className="cf-btn-ghost h-9 px-2 hover:text-red-400" onClick={remove} data-testid="printer-delete-button"><Trash2 size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
